import React from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Reveal from "@/components/Reveal";
import FinalCTA from "@/components/FinalCTA";
import BrochureCTA from "@/components/BrochureCTA";
import { SERVICES } from "@/data/content";
import { buildItemListSchema, buildFAQSchema, SITE_FAQS } from "@/utils/schema";
import Img from "@/components/Img";
import { slotKeyFor } from "@/data/imageSlots";

export default function Services() {
  const itemList = buildItemListSchema(
    SERVICES.map((s) => ({
      name: s.title,
      description: s.blurb || s.summary || "",
      url: `/services#${s.slug}`,
    })),
    "/services"
  );
  const faq = buildFAQSchema(SITE_FAQS);
  return (
    <div data-testid="page-services" className="bg-[#FAF6F1]">

      <SEO
        title="Event Management Services — Weddings, Corporate, Devotional"
        description="Senior-led, end-to-end event management services across India: luxury wedding planning, destination weddings, corporate event management, devotional events, decor, catering, venue discovery, production, photography, and guest management."
        path="/services"
        keywords="event management services, wedding planning services, destination wedding planner, corporate event management, devotional event planner, event decor, catering coordination, venue discovery, event production"
        jsonLd={[itemList, faq]}
      />
<section className="pt-28 md:pt-32 pb-10 bg-[#FAF6F1]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <p className="pill">Services</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[84px] leading-[1.0] mt-6 text-[#3B312D]">
              Wedding and event services, <span className="italic text-[#B65980]">planned beautifully from start to finish.</span>
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[16.5px] text-[#3B312D]/80 leading-relaxed">
              From wedding planning and event décor to corporate events, private celebrations, hospitality,
              and devotional gatherings, Petals &amp; Promises brings every detail together with thoughtful design,
              clear coordination, and calm on-ground execution.
            </p>
          </div>
        </div>
      </section>

      {SERVICES.map((s, idx) => (
        <section key={s.slug} id={s.slug} className={`py-20 md:py-28 ${idx % 2 === 0 ? "bg-[#FAF6F1]" : "bg-[#F4DCD8]/30"}`}>
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <Reveal className={`lg:col-span-6 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
              <div className="rounded-[22px] overflow-hidden image-zoom h-[420px] md:h-[520px]">
                <Img imageKey={slotKeyFor.service(s)} defaultSrc={s.image} defaultAlt={s.title} className="w-full h-full" />
              </div>
            </Reveal>
            <Reveal delay={120} className="lg:col-span-6">
              <p className="pill-rose">Service</p>
              <h2 className="font-display text-4xl md:text-5xl mt-4 text-[#3B312D]">{s.title}</h2>
              <p className="mt-5 text-[16px] leading-relaxed text-[#3B312D]/80 max-w-xl">{s.summary}</p>
              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {s.items.map((it) => (
                  <li key={it} className="flex items-start gap-3 text-[14.5px] text-[#3B312D]">
                    <Check size={16} className="text-[#8A9A7B] mt-1 shrink-0" /> {it}
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="btn-primary mt-9" data-testid={`service-cta-${s.slug}`}>
                Inquire About {s.title} <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
        </section>
      ))}

      <BrochureCTA
        heading="Need the complete service deck"
        text="Download our brochure to explore event categories, planning support, execution layers, and service details across weddings, corporate events, and devotional gatherings."
      />

      <FinalCTA />
    </div>
  );
}
